import { registerView, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import { Alert, Col, Jumbotron, Row } from "react-bootstrap";
import OrderSummaryViewModel from "viewModels/common/payment/orderSummaryViewModel";
import { PaymentType } from "models/payment";
import { ChargeOrderStatus } from "models/chargeOrder";
import { IconType } from "@edaz/edazkit/Icon";
import Button from "@edaz/edazkit/Button";
import { getTimeZoneLabel } from "utils/dateUtils";
import Tooltip from "controls/tooltip";
import { isMobile } from "react-device-detect";

const OrderSummaryView: ViewComponent<OrderSummaryViewModel> = observer(({ vm }) => {
  const tg = (code: string) => vm.localizationService.translateGeneral(`eshop.order.summary.${code}`);
  const renderDateWithTimeZone = (date: Date) => {
    const formatted = vm.localizationService.formatDateTime(date);
    return !vm.isDifferentTimeZone ? (
      <span>{formatted}</span>
    ) : (
      <>
        <span>{formatted}</span>
        <Tooltip
          id="cet-tooltip"
          placement={isMobile ? "bottom" : "right"}
          tooltip={vm.localizationService.translateGeneral("timezoneTooltip")}
          trigger={["hover", "focus"]}>
          <span style={{ marginLeft: 4, textDecoration: "underline", textDecorationStyle: "dotted" }}>
            {getTimeZoneLabel(date)}
          </span>
        </Tooltip>
      </>
    );
  };

  return (
    <Jumbotron
      className={
        "p-5 p-md-7 mb-0 mb-sm-3 mb-md-0 card-box-shadow" +
        (vm.authorizationCode && vm.chargeOrder.paymentType !== PaymentType.Transfer ? " h-100" : "")
      }>
      <div>
        <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col lg="auto" className="m-0 p-0">
            <span className="font-weight-bold text-nowrap">{tg("vehicle")}</span>
          </Col>
          <Col lg="auto" className="m-0 p-0">
            <span className="text-nowrap">{vm.vehicleInfo}</span>
          </Col>
        </Row>
        <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col lg="auto" className="m-0 p-0">
            <span className="font-weight-bold text-nowrap">{tg("markType")}</span>
          </Col>
          <Col lg="auto" className="m-0 p-0">
            <span className="text-nowrap">{vm.chargeType}</span>
          </Col>
        </Row>
        <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col lg="auto" className="m-0 p-0">
            <span className="font-weight-bold text-nowrap">{tg("fuelType")}</span>
          </Col>
          <Col lg="auto" className="m-0 p-0">
            <span className="text-nowrap">{vm.fuelType}</span>
          </Col>
        </Row>
        <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col lg="auto" className="m-0 p-0">
            <span className="font-weight-bold text-nowrap">{tg("validSince")}</span>
          </Col>
          <Col lg="auto" className="m-0 p-0">
            <span className="text-nowrap">
              {vm.validSince
                ? renderDateWithTimeZone(new Date(vm.validSince))
                : vm.localizationService.translateAttribute("chargeType", "undefined")}
            </span>
          </Col>
        </Row>
        <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col lg="auto" className="m-0 p-0">
            <span className="font-weight-bold text-nowrap">{tg("validUntil")}</span>
          </Col>
          <Col lg="auto" className="m-0 p-0">
            <span className="text-nowrap">
              {vm.validUntil
                ? renderDateWithTimeZone(new Date(vm.validUntil))
                : vm.localizationService.translateAttribute("chargeType", "undefined")}
            </span>
          </Col>
        </Row>
        <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col lg="auto" className="m-0 p-0">
            <span className="font-weight-bold">{tg("email")}</span>
          </Col>
          <Col lg="auto" className="m-0 p-0">
            <span className="text-nowrap">{vm.email}</span>
          </Col>
        </Row>
      </div>
      {vm.chargeOrder._proposedAfterPurchaseRedirect && (
        <div className="justify-content-center pt-4 pt-lg-4 pt-xl-4 row">
          <Button
            type="button"
            variant="primary"
            className="px-2 px-md-4"
            onClick={vm.openRedirectLink}
            iconRight={IconType.ArrowRight}>
            {`${tg("openExternalUrl")} ${vm.chargeOrder._proposedAfterPurchaseRedirect.redirectWebsiteName} ${tg(
              "openExternalUrlEnd"
            )}`}
          </Button>
        </div>
      )}
    </Jumbotron>
  );
});

registerView(OrderSummaryView, OrderSummaryViewModel);
